import { Helmet } from "react-helmet-async";

export default function FitterAISupport() {
  return (
    <div className="min-h-screen bg-[#FBFAF6] text-[#26313B] p-8 md:p-16 transition-colors duration-300">
      <Helmet>
        <title>Support – FitterAI</title>
        <meta
          name="description"
          content="Get help and support for FitterAI, the AI-powered fitness coach by Cosmovex."
        />
        <link
          rel="canonical"
          href="https://cosmovexapps.com/apps/fitterai/support"
        />
      </Helmet>

      <h1 className="text-4xl font-bold mb-6 text-[#A8720E]">
        Support – FitterAI
      </h1>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        Need help with <strong>FitterAI</strong>? Find answers below or reach out
        to us directly.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">Contact Us</h2>

      <p className="text-[#3D4B59] leading-relaxed mb-6 max-w-4xl">
        Email:{" "}
        <a
          href="mailto:support@cosmovex.com?subject=FitterAI%20Support"
          className="text-[#0F6BBF] underline hover:text-[#0C5AA3]"
        >
          support@cosmovex.com
        </a>
        <br />
        We typically respond within 24–48 hours.
      </p>

      <h2 className="text-2xl font-semibold mt-10 mb-4">
        Frequently Asked Questions
      </h2>

      <div className="space-y-6 max-w-4xl">
        <div>
          <h3 className="text-lg font-semibold mb-2">How does AI generate my workout plan?</h3>
          <p className="text-[#3D4B59] leading-relaxed">
            FitterAI uses Google&apos;s Gemini AI to create personalized workout
            plans based on your goals, fitness level, available equipment, and
            training schedule. Plans adapt weekly based on your logged
            performance.
          </p>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-2">How do I cancel my FitterAI Pro subscription?</h3>
          <p className="text-[#3D4B59] leading-relaxed">
            Subscriptions are managed through your device&apos;s app store. On
            iOS, go to Settings → Apple ID → Subscriptions. On Android, go to
            Google Play → Payments & subscriptions. Cancel at least 24 hours
            before your renewal date to avoid being charged.
          </p>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-2">Is FitterAI free to use?</h3>
          <p className="text-[#3D4B59] leading-relaxed">
            Yes! FitterAI offers a generous free tier with AI workout plans
            (2 per week), full workout logging, streaks, the exercise library,
            and basic progress tracking. FitterAI Pro unlocks unlimited AI plans,
            coach chat, form analysis, an ad-free experience, and more.
          </p>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-2">How does the AI voice coach work?</h3>
          <p className="text-[#3D4B59] leading-relaxed">
            During workouts, FitterAI&apos;s voice coach talks you through
            exercises via your earbuds or phone speaker. It announces exercises,
            counts, provides motivation, and celebrates personal records. Choose
            from Calm, Drill Sergeant, or Minimal voice modes in Settings.
          </p>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-2">Are my progress photos private?</h3>
          <p className="text-[#3D4B59] leading-relaxed">
            Yes. Progress photos are stored securely in your personal account and
            are never shared publicly unless you explicitly use the share feature.
            Photos are permanently deleted when you delete your account.
          </p>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-2">How do I delete my account?</h3>
          <p className="text-[#3D4B59] leading-relaxed">
            Go to Settings → Account → Delete Account in the app. This
            permanently removes all your data. You can also request deletion by
            emailing support@cosmovex.com. See our{" "}
            <a
              href="https://cosmovexapps.com/apps/fitterai/account-deletion"
              className="text-[#0F6BBF] underline hover:text-[#0C5AA3]"
            >
              account deletion page
            </a>{" "}
            for details.
          </p>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-2">How do I report a bug?</h3>
          <p className="text-[#3D4B59] leading-relaxed">
            Email support@cosmovex.com with a description of the issue, your
            device model, OS version, and app version (found in Settings). Screenshots
            or screen recordings are very helpful.
          </p>
        </div>

        <div>
          <h3 className="text-lg font-semibold mb-2">Can I suggest a feature?</h3>
          <p className="text-[#3D4B59] leading-relaxed">
            Absolutely! We love hearing from users. Send feature requests to
            support@cosmovex.com with the subject line &ldquo;Feature
            Request&rdquo;. We read every suggestion.
          </p>
        </div>
      </div>

      <p className="text-[#8A93A2] mt-10">
        Last updated: March 19, 2026
      </p>
    </div>
  );
}
